<template>
  <figure class="slidepoise-film">
    <div class="slidepoise-film-frame">
      <video ref="video" class="slidepoise-film-video" :src="started ? videoSource.src : undefined" :poster="videoSource.poster" :controls="started && controlsVisible" :aria-hidden="!started" :tabindex="started ? 0 : -1" :preload="started ? 'metadata' : 'none'" playsinline aria-label="SlidePoise introduction" @play="onPlay" @pause="onPause" @ended="onEnded" @error="onError" />
      <button v-if="!started" ref="playButton" class="slidepoise-film-play slidepoise-cover-trigger" type="button" aria-label="Play the SlidePoise video, 2 minutes 10 seconds" @click="play">
        <SlidePoiseFilmCover />
        <span class="slidepoise-play-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m9 6 9 6-9 6Z" /></svg></span>
        <span class="slidepoise-play-label" aria-hidden="true">Watch video<small>2 min 10 sec</small></span>
      </button>
    </div>
    <figcaption v-if="failed" class="slidepoise-film-error" role="status">
      The video could not play. <a :href="videoSource.src" target="_blank" rel="noopener">Open the video</a>
    </figcaption>
  </figure>
</template>

<script>
import SlidePoiseFilmCover from './SlidePoiseFilmCover.vue';
import { slidePoiseVideo } from '../../data/slidepoiseSources';
import { watchVideoControls } from '../../../public/slidepoise/docs/site/video-controls.mjs';
import '../../../public/slidepoise/docs/site/video-controls.css';

export default {
  name: 'SlidePoiseFilm',
  components: { SlidePoiseFilmCover },
  props: { active: { type: Boolean, default: true } },
  data: () => ({ started: false, failed: false, controlsVisible: true }),
  computed: { videoSource() { return slidePoiseVideo; } },
  watch: { active(value) { if (!value) this.pause(); } },
  mounted() {
    this._controlsCleanup = watchVideoControls(this.$refs.video, visible => { this.controlsVisible = visible; });
    document.addEventListener('visibilitychange', this.visibilityChanged);
    window.addEventListener('view-summary-play', this.otherAudio);
    this._observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && !document.fullscreenElement && !this.$refs.video?.webkitDisplayingFullscreen) this.pause();
    });
    this._observer.observe(this.$el);
  },
  beforeUnmount() {
    this._controlsCleanup?.();
    this.pause();
    this._observer?.disconnect();
    document.removeEventListener('visibilitychange', this.visibilityChanged);
    window.removeEventListener('view-summary-play', this.otherAudio);
  },
  methods: {
    async play() {
      if (!this.active) return;
      this.started = true;
      this.failed = false;
      await this.$nextTick();
      const video = this.$refs.video;
      if (!this.active || !video) return;
      video.focus({ preventScroll: true });
      try { await video.play(); }
      catch (error) { if (error.name !== 'AbortError') this.failed = true; }
    },
    onPlay() {
      if (!this.active || document.hidden) { this.pause(); return; }
      this._announcedPlayback = true;
      window.dispatchEvent(new CustomEvent('view-summary-play', { detail: this }));
    },
    onPause() {
      if (!this._announcedPlayback) return;
      this._announcedPlayback = false;
      window.dispatchEvent(new CustomEvent('view-summary-pause', { detail: this }));
    },
    onEnded() { this.onPause(); },
    onError() { this.failed = true; this.onPause(); },
    pause() { this.$refs.video?.pause(); this.onPause(); },
    otherAudio(event) { if (event.detail !== this) this.pause(); },
    visibilityChanged() { if (document.hidden) this.pause(); },
  },
};
</script>

<style scoped>
.slidepoise-film{container-type:inline-size;width:100%;min-width:0;margin:0;color:#252c28;font:14px/1.4 'Quattrocento Sans',sans-serif}
.slidepoise-film-frame{position:relative;aspect-ratio:16/9;overflow:hidden;background:#fffefa}
.slidepoise-film-video{display:block;width:100%;height:100%;object-fit:contain}
.slidepoise-film-play{position:absolute;inset:0;display:block;width:100%;height:100%;padding:0;border:0;border-radius:0;background:#fffefa;color:#252c28;cursor:var(--site-interactive-cursor,pointer);-webkit-tap-highlight-color:transparent}
.slidepoise-film-play:focus-visible{outline:2px solid currentColor;outline-offset:-4px}
.slidepoise-film .slidepoise-film-error{display:block;min-height:0;margin:0;padding:8px 0;font:12px/1.4 'Quattrocento Sans',sans-serif;color:var(--site-text-secondary,#77756f)}
.slidepoise-film-error a{color:inherit;text-underline-offset:3px}
</style>
